import type { SYSFeature, SYSTableDefIds } from "../.."
import { SYSConfig, ovl } from "../.."
import { IsComplexType, T } from "../../global/globals"
import {
  _GetFieldListCacheKey,
  _cachedFieldLists,
  getDisplayValue,
} from "../table/helpers"
import type { ControlVisiblity, FieldValueMap } from "./actions"
import { _ValidateField } from "./actions"
import type { SetFieldStateMode } from "./fieldapi"
import {
  FieldCurrentList,
  FieldSetFocus,
  FieldSetVal,
  FieldValidate,
} from "./fieldapi"

import type { SYSField, SYSFormState } from "./fieldapi"

import {
  HookFormFieldsInit,
  HookFormFieldsInit_Type,
  HookLookupsRefresh,
  HookLookupsRefresh_Type,
} from "../../global/hooks"
import { DialogsState } from "../dialog/sysdialogbase"
import { TableGet } from "../table/tableapi"
import type { SYSFormField, Schema } from "./sysformelement"
import {
  ValidationResetContext,
  _setFieldValid,
  setVisibleValidation,
} from "./validators"
import { _controlInit } from "../../helpers"
import { debounceInfo } from "./controls/textbox"
export let validatorsFunctions: { type?: "SYS" | "Custom"; fn?: any } = {}

export const getFormFields = (
  schema: { [key: string]: Schema },
  formFields: { [key: string]: SYSFormField },
  instanceId: string,
  formType: string,
  feature: SYSFeature,
  subFeature: string,
  mode: "Add" | "Update",
  defId: SYSTableDefIds,
  fillSpaceIfNotVisible: boolean
): FieldValueMap => {
  let fields: FieldValueMap = {}
  let def
  if (defId) {
    def = TableGet(defId).def
  }

  Object.keys(formFields).forEach((k) => {
    //always make sure we have a schema and set the defaults
    if (formFields[k].schema === undefined) {
      if (schema && schema[k]) {
        formFields[k].schema = JSON.parse(JSON.stringify(schema[k]))
      } else {
        formFields[k].schema = {
          defaultValue: null,
          maxLength: 99999999,
          nullable: true,
        }
      }
    } else {
      if (formFields[k].schema.defaultValue === undefined) {
        if (schema?.[k]?.defaultValue !== undefined) {
          formFields[k].schema.defaultValue = schema[k].defaultValue
        } else {
          formFields[k].schema.defaultValue = null
        }
      }
      if (formFields[k].schema.maxLength === undefined) {
        if (
          schema?.[k]?.maxLength !== undefined &&
          formFields[k].type === "text"
        ) {
          formFields[k].schema.maxLength = schema[k].maxLength
        } else {
          formFields[k].schema.maxLength = 99999999
        }
      }
      if (formFields[k].control === undefined) {
        if (formFields[k].list && formFields[k].list.mode === "Option") {
          formFields[k].control = "option"
        } else if (formFields[k].list) {
          formFields[k].control = "list"
        } else {
          formFields[k].control = "text"
        }
      }

      if (formFields[k].order === undefined) {
        formFields[k].order = 0
      }

      if (formFields[k].schema.nullable === undefined) {
        if (schema?.[k]?.nullable !== undefined) {
          formFields[k].schema.nullable = schema[k].nullable
        } else {
          formFields[k].schema.nullable = true
        }
      }
    }

    // was troublesome because:
    // not nullable fields which are null (eg. DocNum in B1 SalesOrder when Add. Its filled after Adding)
    // // now if there is schema and nullable is false in the schema then it can not be configured otherwise
    // // remember: schema comes from db (or is overwritten using schema hook) and needs to be fulfilled
    // //
    // if (
    //   mode === "Update" &&
    //   schema &&
    //   schema[k] &&
    //   schema[k].nullable === false &&
    //   formFields[k].schema.nullable === true
    // ) {
    //   console.error(
    //     "ovl error: field.schema.nullable cannot be set to true if its false in schema. it will be set to false"
    //   )
    //   formFields[k].schema.nullable = false
    // }

    if (formFields[k].schema.nullValidatorToUse === undefined) {
      if (!formFields[k].schema.nullable) {
        //   formFields[k].schema.nullValidatorToUse = "OvlAllowEmpty"
        // } else {
        formFields[k].schema.nullValidatorToUse = SYSConfig._system
          .defaultNullValidatorToUse
          ? SYSConfig._system.defaultNullValidatorToUse
          : "Mandatory"
      }
    }

    let type = formFields[k].type
    if (!type) {
      if (schema && schema[k]) {
        type = schema[k].type
      } else {
        type = "text"
      }
    }
    formFields[k].type = type

    _controlInit(formFields[k])

    if (
      formFields[k].value === undefined &&
      formFields[k].schema.defaultValue !== undefined
    ) {
      formFields[k].value = formFields[k].schema.defaultValue
    }

    if (
      formFields[k].control === "checkbox" &&
      (formFields[k].value === undefined || formFields[k].value === null) &&
      formFields[k].ui?.checkedValue &&
      formFields[k].schema.nullable === false
    ) {
      //for checkboxes there is strictly checkedValue/uncheckedValue if schema says its not nullable
      formFields[k].value = formFields[k].ui?.uncheckedValue
    }

    if (formFields[k].value === undefined) {
      formFields[k].value = null
    }
    if (formFields[k].groups === undefined) {
      formFields[k].groups = {
        tab: 0,
        group: 0,
      }
    }

    if (IsComplexType(formFields[k].value)) {
      throw Error(
        `ovl formfield init error: no complext types allowed:feature: ${feature} field: ${k}`
      )
    }

    if (formFields[k].groups.group === undefined) {
      formFields[k].groups.group = 0
    }
    if (formFields[k].groups.tab === undefined) {
      formFields[k].groups.tab = 0
    }

    if (formFields[k].nullHandling === undefined) {
      formFields[k].nullHandling = {
        enabled: false,
      }
    }

    if (formFields[k].control === "asset" && !formFields[k].editor) {
      formFields[k].editor = { id: "SYSEditorAsset" }
    }
    // check nullHandling. it only can be set to null if underlying schema allows it
    if (formFields[k].nullHandling.enabled) {
      if (formFields[k].schema.nullable === false) {
        throw new Error(
          `ovl error: column ${k} nullHandling.enabled cannot be set to null because of schema violation`
        )
      }
    }
  })

  let formFieldsInitHook =
    ovl.getActions().app.features[feature]?.hooks?.[
      HookFormFieldsInit.replace("{subFeature}", subFeature)
    ]
  if (formFieldsInitHook) {
    formFieldsInitHook(<HookFormFieldsInit_Type>{
      formFieldsToSet: formFields,
    })
  }
  Object.keys(formFields).forEach((k) => {
    if (formFields[k].ui === undefined) {
      formFields[k].ui = {}
    }
    if (formFields[k].ui.hasLineBreaks === undefined) {
      formFields[k].ui.hasLineBreaks = formFields[k]?.control === "textarea"
    }
    if (formFields[k].ui?.alwaysReadOnly) {
      formFields[k].ui.readonly = true
    }

    let value = formFields[k].value
    let type = formFields[k].type
    if (!formFields[k].list && value !== null) {
      if (formFields[k].type === "datetime") {
        if (value !== null && value.length > 16) {
          value = value.replace("+00:00", "Z")
          let dt = new Date(value)
          let t = dt.getTimezoneOffset()
          var offsetTime = new Date(dt.getTime() - t * 60 * 1000)
          value = offsetTime.toISOString().substring(0, 16)
        }
      } else {
        value = getDisplayValue(
          k,
          { list: undefined, type, ui: formFields[k].ui },
          value,
          feature,
          /* we need this fake formstate so displayEmptyIfValue works */
          <SYSFormState>{ firstInitOrSituationChanged: true }
        )
      }
    }
    fields[k] = {
      control: formFields[k].control,
      order: formFields[k].order,
      valid: true,
      value,
      nullHandling: {
        enabled: formFields[k].nullHandling.enabled,
        setToNullIf: formFields[k].nullHandling.setToNullIf,
        setToValueIfNull: formFields[k].nullHandling.setToValueIfNull,
      },
      groups: {
        tab: formFields[k].groups.tab,
        group: formFields[k].groups.group,
      },
      convertedValue:
        formFields[k].type === "object"
          ? JSON.parse(formFields[k].value)
          : formFields[k].value,
      originalValue:
        formFields[k].type === "object"
          ? JSON.parse(formFields[k].value)
          : formFields[k].value,
      previousSavedValidConvertedValue:
        formFields[k].type === "object"
          ? JSON.parse(formFields[k].value)
          : formFields[k].value,

      previousValid: true,
      previousValidConvertedValue:
        mode === "Add" ? undefined : formFields[k].value,
      previousConvertedValue: mode === "Add" ? undefined : formFields[k].value,
      previousValue: undefined,
      type: type,
      schema: formFields[k].schema,

      watched: false,
      touched: false,
      customState: {},
      validationResult: {
        System: [],
        Validate: [],
        ValueChanged: [],
      },
      id: `${formType}_${instanceId}_${k}`,
      list: formFields[k].list,

      editor: formFields[k].editor,
      formId: instanceId,
      formType,
      fieldKey: k,

      ui: formFields[k].ui,
      inactive: formFields[k].inactive,
      hasFocus: false,
      /*ignoreForFormValid: formFields[k].ignoreForFormValid,*/
      _state: {
        forceSave: false,
        closing: false,
        visible: formFields[k].ui?.visible
          ? formFields[k].ui.visible === "true" ||
            formFields[k].ui.visible === "fadeIn"
          : formFields[k].inactive !== true,

        removedAndIrrelevantInCurrentSituation: false,
        forceChange: false,
        valueAfterGotFocus: undefined,
      },
    }

    //let schema = fields[k].schema
    // let defaultValidation = schema?.defaultValidationOptions
    // @@todo
    // // do all the checks and also respect default field validation in validators

    // if (defaultValidation) {
    //   let validation = schema?.nullValidatorOptions?.validation
    //   if (validation) {
    //     if (defaultValidation.displayType && !validation.displayType) {
    //       validation.displayType = defaultValidation.displayType
    //     }
    //   }
    // }

    if (formFields[k].inactive === undefined) {
      formFields[k].inactive = false
    }
    if (formFields[k].list) {
      if (formFields[k].list.mode === undefined) {
        if (!formFields[k].list.serverEndpoint) {
          fields[k].list.mode = "Select"
        } else {
          fields[k].list.mode = "List"
        }
      }
      if (formFields[k].list.acceptOnlyListValues === undefined) {
        fields[k].list.acceptOnlyListValues = true
      }
      if (
        formFields[k].list.acceptNotOnlyListValuesIgnoreFilter === undefined
      ) {
        formFields[k].list.acceptNotOnlyListValuesIgnoreFilter =
          fields[k].list.acceptOnlyListValues === false
      }
      if (
        formFields[k].list
          .tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues === undefined
      ) {
        if (
          SYSConfig.formDefaults
            ?.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues
        ) {
          formFields[
            k
          ].list.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues =
            SYSConfig.formDefaults.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues
        } else {
          formFields[
            k
          ].list.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues = "None"
        }
      }

      // if (formFields[k].list.acceptEmpty === undefined) {
      //   fields[k].list.acceptEmpty = false
      // }

      if (
        formFields[k].list.displayField === undefined &&
        formFields[k].list.valueField
      ) {
        formFields[k].list.displayField = formFields[k].list.valueField
      }

      if (formFields[k].list.displayValueField === undefined) {
        fields[k].list.displayValueField =
          formFields[k].list.valueField !== formFields[k].list.displayField
      }
      if (fields[k].list.refreshMode === undefined) {
        fields[k].list.refreshMode = "Refresh"
      }
      if (fields[k].list.selectOnly === undefined) {
        fields[k].list.selectOnly = false
      }
      if (fields[k].list.showCachedHitsWhileTyping === undefined) {
        if (!fields[k].list.selectOnly) {
          fields[k].list.showCachedHitsWhileTyping = true
        }
      }

      if (fields[k].list.onlyRefreshFromServerIfOlderThanMs === undefined) {
        fields[k].list.onlyRefreshFromServerIfOlderThanMs = 0
      }
      if (fields[k].list.showRefreshFromServerSnack === undefined) {
        fields[k].list.showRefreshFromServerSnack = true
      }

      if (fields[k].list.mode === "Option") {
        if (fields[k].list.acceptOnlyListValuesValidatorOptions === undefined) {
          fields[k].list.acceptOnlyListValuesValidatorOptions = {
            displayType: "OnlyOutline",
          }
        }
        if (
          fields[k].list.acceptOnlyListValuesValidatorOptions.displayType ===
          undefined
        ) {
          fields[k].list.acceptOnlyListValuesValidatorOptions.displayType =
            "OnlyOutline"
        }
      }

      fields[k].list._state = {
        needsRefresh: false,
        lastRefreshTimestamp: 0,
        lastPostParams: undefined,
      }
    }

    if (fields[k].groups === undefined) {
      fields[k].groups = {
        tab: 0,
        group: 0,
      }
    }

    if (fields[k].groups.group === undefined) {
      fields[k].groups.group = 0
    }

    if (fields[k].groups.tab === undefined) {
      fields[k].groups.tab = 0
    }
    let uiWasEmpty = false
    if (!fields[k].ui) {
      fields[k].ui = {}
      uiWasEmpty = true
    }
    let ui = fields[k].ui

    if (ui.readonly === undefined) {
      ui.readonly = false
    }

    if (ui.fillSpaceIfNotVisible === undefined) {
      if (fillSpaceIfNotVisible !== undefined) {
        ui.fillSpaceIfNotVisible = fillSpaceIfNotVisible
      } else {
        ui.fillSpaceIfNotVisible = true
      }
    }

    if (ui.autocomplete === undefined) {
      ui.autocomplete = "off"
    }
    if (ui.useSpellcheck === undefined) {
      ui.useSpellcheck = false
    }
    if (ui.visible === undefined) {
      ui.visible = "true"
    }
    if (ui.visible === "fadeOut" || ui.visible === "false") {
      fields[k]._state.visible = false
    }
    if (ui.checkedValue === undefined) {
      ui.checkedValue = true
    } else typeof ui.checkedValue === "string"
    {
      if (ui.uncheckedValue === undefined) {
        ui.uncheckedValue = "N"
      }
    }
    if (ui.useBrowserDatePicker === undefined) {
      ui.useBrowserDatePicker = false
    }

    //@ts-ignore
    if (ui.infoValueTranslationCode) {
      //@ts-ignore
      ui.infoValue = T(ui.infoValueTranslationCode)
    }

    if (!ui.labelTranslationKey) {
      let labelTranslationPrefix
      if (def) {
        labelTranslationPrefix =
          def.options.labelAndColumnTitleHandling?.translationPrefix
        if (
          labelTranslationPrefix !== undefined &&
          ovl.getState().ovl.language.translations[
            `${labelTranslationPrefix}${k}`
          ] === undefined
        ) {
          if (
            def.options.labelAndColumnTitleHandling?.fallbackTranslationPrefix
          ) {
            labelTranslationPrefix =
              def.options.labelAndColumnTitleHandling?.fallbackTranslationPrefix
          }
        }
      }
      if (!labelTranslationPrefix) {
        labelTranslationPrefix =
          SYSConfig.app.forms[formType]?.labelTranslationPrefix
        if (
          labelTranslationPrefix !== undefined &&
          ovl.getState().ovl.language.translations[
            `${labelTranslationPrefix}${k}`
          ] === undefined
        ) {
          if (SYSConfig.app.forms[formType]?.fallbackLabelTranslationPrefix) {
            labelTranslationPrefix =
              SYSConfig.app.forms[formType].fallbackLabelTranslationPrefix
          }
        }
      }

      if (labelTranslationPrefix) {
        ui.labelTranslationKey = `${labelTranslationPrefix}${k}`
      } else {
        if (uiWasEmpty) {
          ui.noLabel = true
        }
      }
    }

    if (ui.selectIfFocused === undefined) {
      ui.selectIfFocused = SYSConfig.app.forms[formType]?.fieldSelectIfFocused
    }

    if (fields[k].ui) {
      SetTranslatedLabel(fields[k], k)
    }
    fields[k]._state.autoFillHandled =
      "text,textarea,".indexOf(`${fields[k].control},`) === -1
  })
  return fields
}

export const SetTranslatedLabel = (field: SYSField, columnKey: string) => {
  let caption = undefined
  if (field.ui.noLabel !== true) {
    if (field.ui.labelTranslationKey) {
      caption = T(field.ui.labelTranslationKey)
    }
    if (!caption && field.ui.labelCaption) {
      caption = field.ui.labelCaption
    }
    if (!caption) {
      caption = columnKey
    }
    field.ui.labelCaption = caption
  }
}

export const _setDialogVisible = (
  visible: boolean,
  dialogState: DialogsState
) => {
  // explicit tracking shouldn't be necessary here because we are firing the Reactors now in proxyHandler even if its tracking
  // (moved the logic to ignore rendering below rectors call)
  // try {
  //_RenderTracking.forceTracking = true
  dialogState.visible = visible
  if (visible === false) {
    dialogState.closing = false
  }
  // } finally {
  //   //_RenderTracking.forceTracking = false
  // }
}

export const _setFieldReadonly = (field: SYSField, readOnly: boolean) => {
  if (field.ui.alwaysReadOnly) {
    readOnly = true
  }
  field.ui.readonly = readOnly
}

export const _setFieldInactive = async (
  field: SYSField,
  inactive: boolean,
  animate?: "fade" | "none",
  mode?: SetFieldStateMode,
  focus?: boolean,
  calledFromGroup?: boolean,
  forceValueWhenInactive?: any,
  forceValue?: any,
  keepValidation?: boolean,
  clearList?: boolean,
  forceFocus?: boolean,
  selectIfFocusSet?: boolean,
  noRetrigger?: boolean
) => {
  // early bail out if not relevant
  if (field._state.removedAndIrrelevantInCurrentSituation) {
    return
  }

  field.inactiveButRelevantForValidation = !!keepValidation

  let formState: SYSFormState =
    ovl.getState().ovl.forms[field.formType][field.formId]

  try {
    if (!calledFromGroup) {
      // if field belongs to a group and group is inactive do not allow modification
      if (formState.groups.edit[field.groups.group].inactive) {
        return
      }
    }

    // important: if field switched to inactive then do forceval before fild.inactive is set

    if (forceValue !== undefined) {
      if (field.convertedValue !== forceValue) {
        await FieldSetVal(field, forceValue, { userTriggered: noRetrigger })
      }

      // introduced because when selecting a vlue which has formchanged and sets a force value this field should be revisited?
      // if it's not a good behaviour consider to introduce forceWatched analogous forceValue in FieldSetState
      // same further down for whenInactive
      _setFieldWatched(field, false)
      // reset previousvalid... because this was a non user change and if user changes it again it validValueChanged should be true
      field.previousValidConvertedValue = undefined
    }

    if (field.list && clearList) {
      _clearFieldList(field)
    }

    // some defaults
    if (animate === undefined) {
      animate = "fade"
    }
    if (mode === undefined) {
      mode = "visible/notvisible"
    }

    let wasActive = !field.inactive
    let activeChanged = !!field.inactive !== inactive
    let validated = false
    if (!activeChanged) {
      if (inactive) {
        if (!field.inactiveButRelevantForValidation) {
          field.validationResult = {
            System: [],
            Validate: [],
            ValueChanged: [],
          }
        }

        if (forceValueWhenInactive !== undefined) {
          if (
            field.convertedValue !== forceValueWhenInactive ||
            field.value !== forceValueWhenInactive
          ) {
            await FieldSetVal(field, forceValueWhenInactive, {
              userTriggered: noRetrigger,
            })
            validated = true
          }
          // reset previousvalid... because this was a non user change and if user changes it again it validValueChanged should be true
          field.previousValidConvertedValue = undefined
        }
      }
    } else {
      field.inactive = inactive

      if (inactive) {
        _setFieldWatched(field, false)
        if (forceValueWhenInactive !== undefined) {
          if (
            field.convertedValue !== forceValueWhenInactive ||
            field.value !== forceValueWhenInactive
          ) {
            await FieldSetVal(field, forceValueWhenInactive, {
              userTriggered: noRetrigger,
            })

            validated = true
          }
          // reset previousvalid... because this was a non user change and if user changes it again it validValueChanged should be true
          field.previousValidConvertedValue = undefined
        }

        // fieldInactiveButRelevantFirValidation fields will also be saved even if they are inactive
        // and because we would like to save only valid values to our backend according to definition in schema and clientcolumn it's a good idea to keep the validation for those
        if (!field.inactiveButRelevantForValidation) {
          field.validationResult = {
            System: [],
            Validate: [],
            ValueChanged: [],
          }
        }
      }

      //if (field.inactive !== inactive) {
      // if (!wasActive) {
      //   //reset watched
      //   //new 30.03.2023 we assume that if a field switches from inactive to active state we also reset the watched flag
      //   //because: if it was invisible and now appears (or reappear)the user needs to use the field again before its again watched
      //   _setFieldWatched(field, false)
      // }
      // field.inactive = inactive

      //if (!field.inactive || field.inactiveButRelevantForValidation) {
      // if (!validated) {
      //   if (field.list) {
      //     field.value = field.convertedValue
      //   }
      //   await _ValidateField(
      //     field,
      //     formState,
      //     ovl.getActions(),
      //     false,
      //     false,
      //     true
      //   )
      //   validated = true
      // }
      //}
      //}
    }
    if (!validated && ((inactive && keepValidation) || !inactive)) {
      if (field.list) {
        field.value = field.convertedValue
      }
      await _ValidateField(
        field,
        formState,
        ovl.getActions(),
        false,
        false,
        true
      )
      validated = true
    }
    if (!calledFromGroup) {
      if (inactive) {
        if (mode === "visible/notvisible" || mode === "readonly/notvisible") {
          _setFieldVisibility(field, animate === "fade" ? "fadeOut" : "false")
        } else {
          //it should be visible but readonly
          _setFieldVisibility(field, animate === "fade" ? "fadeIn" : "true")
          _setFieldReadonly(field, true)
        }
      } else {
        _setFieldVisibility(field, animate === "fade" ? "fadeIn" : "true")
        if (mode === "readonly/notvisible") {
          _setFieldReadonly(field, true)
        } else {
          _setFieldReadonly(field, false)
          if (
            (forceFocus || (!wasActive && focus)) &&
            !formState.firstInitOrSituationChanged
          ) {
            setTimeout(() => {
              FieldSetFocus(field, selectIfFocusSet)
            }, 200)
          }
        }
      }
    } else {
      if (
        (forceFocus || (!wasActive && focus)) &&
        !formState.firstInitOrSituationChanged
      ) {
        setTimeout(() => {
          FieldSetFocus(field, selectIfFocusSet)
        }, 200)
      }
    }
  } finally {
    _setFieldValid(field)
    setVisibleValidation(formState)
  }
}

export const _clearFieldList = (field: SYSField) => {
  let formState = ovl.getState().ovl.forms[field.formType][field.formId]
  let functionName = HookLookupsRefresh
  let fn = ovl.getActions().app.features[formState.feature]
  if (fn?.hooks?.[functionName]) {
    let lookupsState = fn.hooks[functionName](<HookLookupsRefresh_Type>{
      formState,
      tableDefId: formState.tableDefId,
    })
    if (!lookupsState) {
      return
    }
    lookupsState[field.list.valueField].data = {}
    field.list._state.lastRefreshTimestamp = 0
  }
}

export const _setFieldTouched = (field: SYSField, touched: boolean) => {
  if (field.touched !== touched) {
    ovl.getActions().ovl.internal._SetFieldTouched({ field, touched })
  }
}

export const _setFieldWatched = (field: SYSField, watched: boolean) => {
  if (field.watched !== watched) {
    ovl.getActions().ovl.internal._WatchField({ field, watched })
    setVisibleValidation(ovl.getState().ovl.forms[field.formType][field.formId])
  }
}

export const _setFieldFocus = (field: SYSField, focus: boolean) => {
  if (field.hasFocus !== focus) {
    ovl.getActions().ovl.internal._FocusField({ field, hasFocus: focus })
  }
}

export const _setFieldVisibility = (
  field: SYSField,
  visible: ControlVisiblity
) => {
  if (
    field.ui.visible === visible ||
    (field._state.closing && visible === "fadeOut")
  ) {
    return
  }
  field.ui.visible = visible
  // if field disappears it will be watched false (because it could reappear but user didnt see it)
  if (visible === "false" || visible === "fadeOut") {
    _setFieldWatched(field, false)
  }

  let el = document.getElementById("ovl_" + field.id)
  if ((!field._state.visible || field._state.closing) && visible === "fadeIn") {
    if (field.inactive) {
      field.inactive = false
    }
    if (el) {
      el.classList.remove("fadeOutControl")
      el.classList.remove("ovl-hideControl")
      el.classList.add("fadeInControl")
      // if (focus) {
      //   setTimeout(() => {
      //     SetFocus(field.id)
      //   }, 200)
      // }
    } else {
      // we are accessing the el from another screen probably. so el is not defined...no fadeIn required just set to true
      // same below for fadeOut from another screen...
      field._state.visible = true
    }
  } else if (visible === "true") {
    if (field.inactive) {
      field.inactive = false
    }

    if (el) {
      el.classList.remove("fadeInControl")
      el.classList.remove("fadeOutControl")
      el.classList.remove("ovl-hideControl")
    }
    field._state.visible = true

    // if (focus) {
    //   setTimeout(() => {
    //     SetFocus(field.id)
    //   }, 200)
    // }
  } else if (field._state.visible && visible === "fadeOut") {
    if (el) {
      el.classList.remove("fadeInControl")
      el.classList.add("fadeOutControl")
    } else {
      field._state.visible = false
    }
  } else if (visible === "false") {
    if (el) {
      el.classList.remove("fadeInControl")
      el.classList.remove("fadeOutControl")

      el.classList.add("ovl-hideControl")
    }
    field._state.visible = false
  }
}

export const _resetValidationForContext = (
  formState: SYSFormState,
  field: SYSField,
  context: ValidationResetContext
) => {
  let fields = formState.fields
  Object.keys(fields).forEach((k) => {
    if (k === field.fieldKey) {
      return
    }
    let _field = fields[k]
    let indexesToDelete: Set<number> = new Set()
    _field.validationResult[context].forEach((v, i) => {
      if (v.handledFieldKeys) {
        v.handledFieldKeys.forEach((k) => {
          if (k === field.fieldKey) {
            indexesToDelete.add(i)
          }
        })
      }
    })
    if (indexesToDelete.size > 0) {
      _field.validationResult[context] = field.validationResult[context].filter(
        (f, i) => !indexesToDelete.has(i)
      )
    }
  })
  field.validationResult[context] = []
}

export const _FieldListBustCache = (field: SYSField) => {
  if (!field?._state) {
    return
  }
  let formState: SYSFormState =
    ovl.getState().ovl.forms[field.formType][field.formId]
  let cacheKey = _GetFieldListCacheKey(
    field.fieldKey,
    formState.tableDefId,
    formState
  )
  _cachedFieldLists.set(cacheKey, undefined)
}

export function sortedReplacer(_key, value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.keys(value)
      .sort()
      .reduce((obj, k) => {
        obj[k] = value[k]
        return obj
      }, {})
  }
  return value
}

export const _SetCorrectedValue = async (
  val: any,
  field: SYSField,
  ignoreCustomValidation = false,
  selectValue = false
) => {
  // for field lists its always meant to be the convertedValue
  if (field.list) {
    field.convertedValue = val
  } else {
    if (val !== null && typeof val !== "string") {
      val = val.toString()
    }
  }
  // new 27.10.2025, if it doesnt work uncomment part after and remove this
  if (debounceInfo.inputDebounce) {
    clearTimeout(debounceInfo.inputDebounce)
  }
  let el = document.getElementById(field.id)
  if (el) {
    if (!field.list) {
      //@ts-ignore
      el.value = val
    }
    //@ts-ignore
    if (selectValue && el.select) {
      //@ts-ignore
      el.select()
    }
  }
  field._state.autoFillHandled = true
  field._state.valueAfterGotFocus = field.value
  //field._state.valueChangedHandled = true
  field.value = val
  // we still need to validate otherwise an eg. mandatory field will not be flagged correctly
  await FieldValidate(field, ignoreCustomValidation)
}

export const _TryGetLookupFromServerInFormInitIfNotFound = (
  field: SYSField,
  checkValue: any
) => {
  if (
    field.list &&
    field.list.serverEndpoint &&
    field.list.tryGetLookupFromServerInFormInitIfNotFound
  ) {
    let existingList = FieldCurrentList(field)
    if (
      checkValue === null ||
      (field.list.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues !==
        "None" &&
        field.list.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues.indexOf(
          checkValue
        ) > -1)
    ) {
      return false
    }
    if (existingList.data[checkValue] !== undefined) {
      // in addition if one of the lookup column is undefined it needs to be completed as well using this method
      return Object.keys(existingList.lookupDef).some(
        (s) => existingList.data[checkValue][s] === undefined
      )
    } else {
      return true
    }
  } else {
    return false
  }
}
